/**
 * Shared time/validation utilities for work schedule components.
 * Extracted from CreateWorkRangeModal and EditWorkRangeModal.
 */

export function timeToMinutes(hhmm: string): number {
  const [hh, mm] = hhmm.split(':').map(Number);
  return hh * 60 + mm;
}

export function hasOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
  const s1 = timeToMinutes(start1);
  let e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  let e2 = timeToMinutes(end2);

  if (e1 === 0) e1 = 24 * 60;
  if (e2 === 0) e2 = 24 * 60;

  return s1 < e2 && e1 > s2;
}

export const TIMES_30: string[] = Array.from({ length: 48 }).map((_, i) => {
  const hh = String(Math.floor(i / 2)).padStart(2, '0');
  const mm = i % 2 === 0 ? '00' : '30';
  return `${hh}:${mm}`;
});

export const START_TIMES_30: string[] = TIMES_30.filter(t => {
  const m = parseInt(t.split(':')[0], 10) * 60 + parseInt(t.split(':')[1], 10);
  return (24 * 60 - m) >= 60;
});

export function getOccupiedIntervals(
  rowIndex: number,
  allRanges: Array<{ startTime: string; endTime: string }>,
  schedulesForDay: Array<{ startTime: string; endTime: string }>,
): Array<{ start: number; end: number }> {
  const occupied: Array<{ start: number; end: number }> = [];
  for (let i = 0; i < allRanges.length; i++) {
    if (i === rowIndex) continue;
    const r = allRanges[i];
    if (!r.startTime || !r.endTime) continue;
    const s = timeToMinutes(r.startTime);
    const e = timeToMinutes(r.endTime) === 0 ? 24 * 60 : timeToMinutes(r.endTime);
    occupied.push({ start: s, end: e });
  }
  for (const s of schedulesForDay) {
    const start = timeToMinutes(s.startTime);
    const end = timeToMinutes(s.endTime) === 0 ? 24 * 60 : timeToMinutes(s.endTime);
    occupied.push({ start, end });
  }
  return occupied;
}

export function getFilteredStartOptions(
  occupied: Array<{ start: number; end: number }>,
  endTime?: string,
): string[] {
  const endMin = endTime ? (timeToMinutes(endTime) === 0 ? 24 * 60 : timeToMinutes(endTime)) : null;
  return START_TIMES_30.filter(t => {
    const m = timeToMinutes(t);
    if (endMin !== null && m >= endMin) return false;
    if (endMin !== null && endMin - m < 60) return false;
    if (occupied.some(o => m >= o.start && m < o.end)) return false;
    let nextStart = 24 * 60;
    for (const o of occupied) {
      if (o.start > m && o.start < nextStart) nextStart = o.start;
    }
    if (nextStart - m < 60) return false;
    return true;
  });
}

export function getFilteredEndOptions(
  start: string,
  occupied: Array<{ start: number; end: number }>,
): string[] {
  if (!start) return TIMES_30;
  const startMin = timeToMinutes(start);
  const minEndMin = startMin + 60;

  let maxEndMin = 24 * 60;
  for (const o of occupied) {
    if (o.start > startMin && o.start < maxEndMin) {
      maxEndMin = o.start;
    }
  }

  const filtered = TIMES_30.filter(t => {
    const tMin = timeToMinutes(t);
    if (tMin === 0) return false;
    return tMin >= minEndMin && tMin <= maxEndMin;
  });
  if (startMin >= 23 * 60 && (24 * 60 - startMin) >= 60 && maxEndMin === 24 * 60) {
    filtered.push('00:00');
  }
  return filtered;
}

export function validateRange(
  range: { startTime: string; endTime: string },
  index: number,
  allRanges: Array<{ startTime: string; endTime: string }>,
  schedulesForDay: Array<{ startTime: string; endTime: string }>,
): string {
  const { startTime: st, endTime: et } = range;
  if (!st || !et) return '';
  const startMinRaw = timeToMinutes(st);
  const endMinRaw = timeToMinutes(et);
  if (startMinRaw % 30 !== 0 || endMinRaw % 30 !== 0) {
    return 'Los horarios deben estar en intervalos de 30 minutos';
  }
  const endMin = endMinRaw === 0 ? 24 * 60 : endMinRaw;
  if (endMin <= startMinRaw) {
    return 'La hora de fin debe ser posterior a la hora de inicio';
  }
  const overlapping = schedulesForDay.find(s => hasOverlap(st, et, s.startTime, s.endTime));
  if (overlapping) {
    return `Se solapa con horario existente: ${overlapping.startTime} - ${overlapping.endTime}`;
  }
  for (let j = 0; j < allRanges.length; j++) {
    if (j === index) continue;
    const other = allRanges[j];
    if (!other.startTime || !other.endTime) continue;
    if (hasOverlap(st, et, other.startTime, other.endTime)) {
      return `Se solapa con otro rango: ${other.startTime} - ${other.endTime}`;
    }
  }
  return '';
}
